import { Component, type ErrorInfo, type ReactNode } from "react";
import { reportClientError } from "../lib/errorReporting";

interface Props {
  children: ReactNode;
}

interface State {
  failed: boolean;
  message: string;
}

export class AppErrorBoundary extends Component<Props, State> {
  state: State = { failed: false, message: "" };

  static getDerivedStateFromError(error: Error): State {
    return { failed: true, message: error?.message || "Unknown application error" };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    const errorWithComponentContext = new Error(error.message);
    errorWithComponentContext.name = error.name;
    errorWithComponentContext.stack = [error.stack, info.componentStack].filter(Boolean).join("\n");
    reportClientError(errorWithComponentContext, "react-boundary");
  }

  private recoverDashboard = () => {
    // Rendering failures do not prove that device storage is corrupt. Reload
    // without removing preferences, authentication or push registration.
    window.location.replace(`/?reload=${Date.now()}`);
  };

  render() {
    if (this.state.failed) {
      return (
        <main className="fatal-error-shell">
          <section className="fatal-error-card" role="alert">
            <p className="eyebrow">FamDashFun</p>
            <h1>We hit a snag.</h1>
            <p>Something went wrong displaying this page. Reload the dashboard to try again.</p>
            <button type="button" onClick={this.recoverDashboard}>Reload dashboard</button>
            {this.state.message && <small className="fatal-error-detail">Error: {this.state.message}</small>}
          </section>
        </main>
      );
    }

    return this.props.children;
  }
}

